import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const OceanShipment = ({ shipmentData }) => {
  const { shipmentEvents, shipmentLegs, ...rest } = shipmentData;

  const renderDetails = () => {
    const containerNumber = rest.containerNumber || rest.trackingNumber || 'N/A';
    const containerType = rest.containerType || 'N/A';
    const status = rest.status || 'N/A';
    const mblNumber = rest.mblNumber || 'N/A';
    const bookingNumber = rest.bookingNumber || 'N/A';
    const carrier = shipmentLegs?.portToPort?.carrier || rest.carrier || 'N/A';
    const carbonEmissions = rest.emissions?.co2e?.value 
      ? `${rest.emissions.co2e.value} ${rest.emissions.co2e.unit}` 
      : 'N/A';

    return (
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>Shipment Details</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Container #</strong></TableCell>
                <TableCell><strong>Container Type</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>MBL #</strong></TableCell>
                <TableCell><strong>Booking #</strong></TableCell>
                <TableCell><strong>Carrier</strong></TableCell>
                <TableCell><strong>Carbon Emissions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{containerNumber}</TableCell>
                <TableCell>{containerType}</TableCell>
                <TableCell>{status}</TableCell>
                <TableCell>{mblNumber}</TableCell>
                <TableCell>{bookingNumber}</TableCell>
                <TableCell>{carrier}</TableCell>
                <TableCell>{carbonEmissions}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderTracking = () => {
    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Tracking</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Shipper</strong></TableCell>
                    <TableCell>{rest.shipper || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Origin Port</strong></TableCell>
                    <TableCell>{rest.originOceanPort || shipmentLegs?.portToPort?.firstPort || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Port Gate In</strong></TableCell>
                    <TableCell>{shipmentEvents?.find(e => e.name === 'Full Container Gate In')?.actualTime || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Port ATD</strong></TableCell>
                    <TableCell>{rest.originOceanPortAtd || shipmentLegs?.portToPort?.firstPortAtd || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Consignee</strong></TableCell>
                    <TableCell>{rest.consignee || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Destination Port</strong></TableCell>
                    <TableCell>{rest.destinationOceanPort || shipmentLegs?.portToPort?.lastPort || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Flow Predicts</strong></TableCell>
                    <TableCell>{rest.destinationOceanPortEta || shipmentLegs?.portToPort?.lastPortEta || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSegments = () => {
    const segments = shipmentLegs?.portToPort?.segments || [];
    if (segments.length === 0) return null;

    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Shipping Segments</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>From</strong></TableCell>
                <TableCell><strong>To</strong></TableCell>
                <TableCell><strong>Transport Name</strong></TableCell>
                <TableCell><strong>Trip #</strong></TableCell>
                <TableCell><strong>Departure</strong></TableCell>
                <TableCell><strong>Arrival</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map((segment, index) => {
                const departure = segment.atd ? `Actl. ${segment.atd.split(' ')[0]}` : segment.etd ? `Estd. ${segment.etd.split(' ')[0]}` : 'N/A';
                const arrival = segment.ata ? `Actl. ${segment.ata.split(' ')[0]}` : segment.eta ? `Estd. ${segment.eta.split(' ')[0]}` : 'N/A';

                return (
                  <TableRow key={index}>
                    <TableCell>{segment.origin || segment.originPortCode || 'N/A'}</TableCell>
                    <TableCell>{segment.destination || segment.destinationPortCode || 'N/A'}</TableCell>
                    <TableCell>{segment.transportName || 'N/A'}</TableCell>
                    <TableCell>{segment.tripNumber || 'N/A'}</TableCell>
                    <TableCell>{departure}</TableCell>
                    <TableCell>{arrival}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderMap = () => {
    const { latitude, longitude } = shipmentLegs?.portToPort?.latestLocation || {};
    if (!latitude || !longitude) return null;

    // return (
    //   <Box mt={4}>
    //     <Typography variant="h6" gutterBottom>Current Location</Typography>
    //     <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
    //       <GoogleMap
    //         mapContainerStyle={{ width: '100%', height: '400px' }}
    //         center={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }}
    //         zoom={10}
    //       >
    //         <Marker position={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }} />
    //       </GoogleMap>
    //     </LoadScript>
    //   </Box>
    // );
  };

  return (
    <>
      {renderDetails()}
      {renderTracking()}
      {renderSegments()}
      {renderMap()}
    </>
  );
};

export default OceanShipment;